import { create } from "zustand";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { ContextConfig, DashboardTabModel } from "api/model/dashboard/DashboardTab";

interface DashboardState {
  dashboard: Dashboard | undefined;
  tab: DashboardTabModel | undefined;
  isEditMode: boolean;
  tabId: number;
  refreshKey: number;
  tabContextConfigs: ContextConfig[];
  isGroupReorderEnabled: boolean;
  setDashboard: (dashboard: Dashboard) => void;
  setTab: (tab: DashboardTabModel) => void;
  setIsEditMode: (isEditMode: boolean) => void;
  triggerTabRefresh: () => void;
  getTab: (tabId: number) => DashboardTabModel | undefined;
  getTabContextConfig: (tabId: number) => any;
  setTabContextConfig: (tabId: number, tabContextConfig: ContextConfig) => void;
  updateComponent: (
    groupId: string,
    componentId: string,
    config: any,
    tabId: number,
    refresh?: boolean
  ) => void;
  removeComponent: (groupId: string, componentId: string, tabId: number) => void;
  moveComponent: (
    sourceGroupId: string,
    componentId: string,
    targetGroupId: number,
    sourceTabId: number,
    targetTabId: number
  ) => void;
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  dashboard: undefined,
  tab: undefined,
  isEditMode: false,
  tabId: 0,
  refreshKey: 0,
  tabContextConfigs: [],
  isGroupReorderEnabled: false,

  setDashboard: (dashboard: Dashboard) => set({ dashboard }),
  setTab: (tab: any) => set({ tab }),
  setIsEditMode: (isEditMode: boolean) => set({ isEditMode }),
  triggerTabRefresh: () => set(state => ({ refreshKey: state.refreshKey + 1 })),
  getTab: (tabId: number) => get().dashboard?.dashboardTabsJs?.[tabId],
  getTabContextConfig: (tabId: number) =>
    get().tabContextConfigs[tabId] || get().dashboard?.dashboardTabsJs?.[tabId]?.contextConfig,
  setTabContextConfig: (tabId: number, config: ContextConfig) =>
    set(state => {
      const newContextConfig = { ...state.tabContextConfigs, [tabId]: config };
      return { tabContextConfigs: newContextConfig };
    }),
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => set({ isGroupReorderEnabled }),
  updateComponent: (
    groupId: string,
    componentId: string,
    config: any,
    tabId: number,
    refresh = true
  ) => {
    set(state => {
      const { dashboard } = state;
      if (!dashboard || !dashboard.dashboardTabsJs?.[tabId]) return state;

      const newDashboard = { ...dashboard };
      const tab = newDashboard.dashboardTabsJs[tabId];
      const group = tab.componentGroups?.find(g => g.id === groupId);
      if (!group) return state;

      const updatedComponents = group.components.map(component =>
        component.id === componentId ? { ...component, ...config } : component
      );

      const updatedGroup = { ...group, components: updatedComponents };
      const updatedGroups = tab.componentGroups.map(g => (g.id === groupId ? updatedGroup : g));

      newDashboard.dashboardTabsJs[tabId] = { ...tab, componentGroups: updatedGroups };

      return {
        dashboard: newDashboard,
        refreshKey: refresh ? state.refreshKey + 1 : state.refreshKey,
      };
    });
  },
  moveComponent: (
    sourceGroupId: string,
    componentId: string,
    targetGroupId: number,
    sourceTabId: number,
    targetTabId: number
  ) => {
    const state = get();
    const { dashboard } = state;
    if (!dashboard) return;
    const sourceTab = dashboard.dashboardTabsJs?.[sourceTabId];
    const targetTab = dashboard.dashboardTabsJs?.[targetTabId];
    if (!sourceTab || !targetTab) return;
    const sourceGroup = sourceTab.componentGroups?.find(g => g.id === sourceGroupId);
    const targetGroup = targetTab.componentGroups?.find(g => g.id === targetGroupId.toString());
    if (!sourceGroup || !targetGroup) return;
    const compIndex = sourceGroup.components.findIndex(component => component.id === componentId);
    if (compIndex === -1) return;
    const [movedComponent] = sourceGroup.components.splice(compIndex, 1);
    targetGroup.components.push(movedComponent);
    set({ dashboard, refreshKey: state.refreshKey + 1 });
  },

  removeComponent: (groupId: string, componentId: string, tabId: number) => {
    const state = get();
    const { dashboard } = state;
    if (!dashboard) return;
    const tab = dashboard.dashboardTabsJs?.[tabId];
    const group = tab?.componentGroups?.find(g => g.id === groupId);
    if (!group) return;
    const compIndex = group.components.findIndex(component => component.id === componentId);
    if (compIndex === -1) return;
    group.components.splice(compIndex, 1);
    set({ dashboard, refreshKey: state.refreshKey + 1 });
  },
}));

export const setEditMode = (isEdit: boolean) => {
  useDashboardStore.setState({ isEditMode: isEdit });
};

export const setDashboard = (dashboard: Dashboard | undefined) => {
  useDashboardStore.setState({ dashboard });
};

export default useDashboardStore;
